<template>
  <b-container class="user-network-container p-1">
    <i
      class="fa fa-users group-contact-count-icon"
      aria-hidden="true"
      @click="smallModal = true"
    >
      <span class="group-contact-count-span" v-if="userList.length">
        {{ userNetworkMembersCount }}
      </span>
      <span class="group-contact-count-span desktop-mode"> Contacts</span>
    </i>
    <b-row>
      <b-modal
        v-model="smallModal"
        :title="`${userNetworkMembersCount} Contacts`"
        size="md"
        class="select-company-modal"
        hide-footer
        scrollable
        @click="smallModal = false"
      >
        <div ref="memberListContainer" class="company-select-div">
          <b-input-group class="search-input-con">
            <b-form-input
              id="input-default"
              v-model="memberName"
              placeholder="Name"
              class="search-input"
              debounce="400"
            />
            <div class="input-group-prepend">
              <span class="input-group-text search-icon-block">
                <i class="fa fa-search" />
              </span>
            </div>
          </b-input-group>
          <div class="mt-3 mb-4">
            <multiselect
              ref="multiselect"
              v-model="company"
              :options="allCompanies"
              :multiple="false"
              label="name"
              track-by="id"
              :clear-on-select="false"
              placeholder="company"
              :allow-empty="true"
              :close-on-select="true"
              @open="getCompanies($lodash.isEmpty(allCompanies))"
              @search-change="searchCompany"
            >
              <template v-if="companiesNextPage" slot="afterList">
                <b-row>
                  <b-col class="m-2">
                    <b-button
                      class="load-companies"
                      variant="primary"
                      @click="loadMoreCompanies"
                    >
                      Load More
                    </b-button>
                  </b-col>
                </b-row>
              </template>
            </multiselect>
          </div>
          <b-row
            v-for="{ network_member_details, id } in userList"
            :key="id"
            class="company-select-row"
          >
            <b-col @click="goToProfile(network_member_details.customer_id)">
              <div class="d-flex mt-2 mb-2">
                <img
                  :src="network_member_details.prof_pic"
                  class="company-img"
                />
                <div class="p-1">
                  <p class="user-name">
                    {{ network_member_details.name }}
                  </p>
                  <p class="user-position">
                    {{ network_member_details.job_title }}
                  </p>
                  <p class="user-company-name">
                    {{ network_member_details.company }}
                  </p>
                </div>
              </div>
            </b-col>
          </b-row>
          <ScrollLoader v-if="isFetchingMembers" />
          <div ref="scrollSentinel" class="scroll-sentinel" />
        </div>
      </b-modal>
    </b-row>
  </b-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';

import ScrollLoader from './common/ScrollLoader.vue';
import infiniteScrollObserver from '../utils/mixins/infiniteScrollObserver';

export default {
  name: 'UserNetwork',
  mixins: [infiniteScrollObserver],
  components: {
    Multiselect,
    ScrollLoader,
  },
  props: {
    customerId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      smallModal: false,
      company: null,
      userList: [],
      memberName: '',
      companyId: '',
      // highest page currently in userList
      membersPage: 1,
      nextPage: true,
      isSearchingMembers: false,
      isFetchingMembers: false,
      companiesPage: 1,
      companiesNextPage: true,
      companyName: '',
      allCompanies: [],
      // total contacts, only updated by unfiltered fetches
      userNetworkMembersCount: 0,
      // guards against out-of-order responses when filters change quickly
      membersRequestId: 0,
    };
  },
  computed: {
    ...mapGetters({
      allCompaniesList: 'companies/allCompaniesList',
    }),
  },
  watch: {
    company(val) {
      if (val && val.id) {
        this.companyId = val.id;
      } else {
        this.companyId = '';
      }
    },
    companyId() {
      this.searchMember();
    },
    memberName() {
      this.searchMember();
    },
    smallModal(val) {
      if (!val) {
        this.memberName = '';
        this.company = null;
        this.companyId = '';
      }
    },
  },
  created() {
    this.searchMember();
    this.allCompanies = [...this.allCompaniesList];
  },
  methods: {
    ...mapActions({
      searchCompanyByName: 'companies/searchCompanyByName',
      searchNetworkUser: 'user/searchNetworkUser',
    }),
    goToProfile(id) {
      this.$router.push({ name: 'MemberWall', params: { customerId: id } });
    },
    scrollSentinelConfig() {
      return [{
        ref: 'scrollSentinel',
        root: 'memberListContainer',
        loadMore: this.loadMore,
      }];
    },
    // Always page 1. Previously this omitted membersPage (so the API defaulted to
    // 1) and then pre-incremented the counter, which raced the loader's first
    // fire and sent a second identical page-1 request.
    async searchMember() {
      const { customerId, memberName, companyId } = this;
      this.membersRequestId += 1;
      const requestId = this.membersRequestId;
      this.isSearchingMembers = true;
      try {
        const { results, next, count } = await this.searchNetworkUser({
          customerId,
          memberName,
          companyId,
          membersPage: 1,
        });
        if (requestId !== this.membersRequestId) return;
        this.userList = results;
        this.nextPage = !!next;
        this.membersPage = 1;
        if (!memberName && !companyId) {
          this.userNetworkMembersCount = count;
        }
      } finally {
        if (requestId === this.membersRequestId) {
          this.isSearchingMembers = false;
        }
        // the sentinel may still be within view after the swap, in which case
        // IntersectionObserver won't refire on its own (no visibility change)
        this.$nextTick(() => this.recheckScrollSentinel('scrollSentinel'));
      }
    },
    async loadMore() {
      // isSearchingMembers: a page-1 search in flight is about to replace
      // userList, so appending page 2 on top of it would lose those rows.
      if (this.isFetchingMembers || this.isSearchingMembers || !this.nextPage) return;
      this.isFetchingMembers = true;
      const requestId = this.membersRequestId;
      try {
        const { customerId, memberName, companyId } = this;
        const membersPage = this.membersPage + 1;
        const data = await this.searchNetworkUser({
          customerId,
          memberName,
          companyId,
          membersPage,
        });
        // filters changed mid-flight; this page belongs to the old list
        if (requestId !== this.membersRequestId) return;
        this.nextPage = !!data.next;
        this.membersPage = membersPage;
        this.userList.push(...data.results);
      } catch (err) {
        // allow retry on next intersection
      } finally {
        this.isFetchingMembers = false;
      }
    },
    async getCompanies(isSearch) {
      try {
        if (this.companiesNextPage && isSearch) {
          const { next, results } = await this.searchCompanyByName({
            name: this.companyName,
            page: this.companiesPage,
          });
          this.allCompanies = [...this.allCompanies, ...results];
          this.companiesNextPage = !!next;
          this.companiesPage += 1;
        }
      } catch (err) {
        console.log('err', err);
      }
    },
    async searchCompany(searchQuery) {
      this.companiesNextPage = true;
      this.companiesPage = 1;
      this.companyName = searchQuery;
      this.allCompanies = [];
      await this.getCompanies();
    },
    async loadMoreCompanies() {
      await this.getCompanies(true);
    },
  },
};
</script>

<style scoped>
.sponsors-icon {
  color: #bd9d5f;
}
.company-photo {
  width: 20px;
  height: 20px;
}
.select-company-modal {
  height: 100%;
}
/* max-height, not min-height: this element is the scroll sentinel's root, so it
   needs a real cap for the sentinel to be clipped out of view. The former
   min-height: 500px was both an unbounded root (pages drained on open) and a
   source of blank space when the list was short. */
.company-select-div {
  max-height: 60vh;
  overflow-y: auto;
  overflow-x: hidden;
}
.scroll-sentinel {
  height: 1px;
}
.company-img {
  height: 45px;
  width: 45px;
}
.user-name {
  font-family: Arial;
  font-size: 15px;
  line-height: 1.23;
  text-align: left;
  font-weight: bold;
  color: #333333;
  margin: 0;
}
.user-position {
  font-family: Arial;
  font-size: 10px;
  line-height: 1.23;
  text-align: left;
  font-weight: bold;
  color: #666666;
  margin: 0;
}
.user-company-name {
  font-family: Arial;
  font-size: 9px;
  line-height: 1.23;
  text-align: left;
  font-weight: bold;
  color: #bd9d5f;
  margin: 0;
}
.company-select-row:hover {
  cursor: pointer;
  background-color: #bd9d5f;
}
.search-icon-block {
  height: 30px;
  padding: 6px;
  background-color: transparent;
  border: 1px solid #aaaaaa;
  border-left: none;
}
.search-input {
  box-shadow: none;
  border: 1px solid #aaaaaa;
  border-right: none;
  height: 30px;
}
.search-input:focus {
  border-color: #aaaaaa;
  box-shadow: none;
}
.search-input-con {
  padding-right: 1px;
}
.sponsors-image {
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center center;
  border: 1px solid #ebebeb;
}
.sponsors-image-remove-btn {
  background: #00ffff00;
  border: navajowhite;
  float: right;
}
.sponsors-post-image-delete {
  color: #bd9d5f;
  border-radius: 50%;
  background-color: white;
}
.main-user-info {
  border: 1px solid #e4e5e6;
  display: flex;
  flex-direction: row;
}
.user-img {
  width: 75px;
  height: 75px;
  cursor: pointer;
  float: left;
}
.user-img > img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.username {
  font-weight: bold;
  text-transform: capitalize;
  font-size: 12px;
  margin: 0;
}
.user-position {
  text-transform: capitalize;
  color: #666666;
  font-size: 10px;
}
.created-time {
  color: #9c9999;
  font-size: 10px;
}
.user-info {
  padding: 0 10px;
}
.group-contact-count-icon {
  color: #bd9d5f;
  cursor: pointer;
}
.group-contact-count-span {
  font-family: Arial;
  font-size: 17px;
}
.load-companies {
  width: 100%;
  background: none;
  border: 1px solid #bd9d5f;
  color: #bd9d5f;
  font-size: 12px;
  margin-bottom: 25px;
}
.load-companies:hover {
  background-color: #bd9d5f;
  color: white;
  transition: 0.5s;
}
.user-network-container {
  margin: 0 auto;
}
@media (max-width:768px) {
  .desktop-mode {
    display: none;
  }
  .group-contact-count-span,
  .group-contact-count-icon {
    font-size: 12px;
  }
}
</style>
